import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { LoadingContainer } from "go-core/components/Loading";
import { FILTER_VALUE_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { organizationApi } from "../../../../../../../../services/Api/Organization/organizationApi";
import {
	AvailableSubscriptionContentsApi,
	SubscriptionApi,
	SubscriptionInvoiceApi,
	SubscriptionVisibility,
	SubscriptionsApi,
} from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { OrganizationMeApi } from "../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import SubscriptionsFormNew from "../../components/FormNew/SubscriptionsForm";
import SubscriptionErrorsDisplay from "../../components/SubscriptionErrorsDisplay";
import SubscriptionsExpirationDate from "../../components/SubscriptionsExpirationDate";
import SubscriptionsReadOnlyDisplay from "../../components/SubscriptionsReadOnlyDisplay";

const sortContents = (data: AvailableSubscriptionContentsApi) => {
	return {
		...data,
		contents: data.contents.map((content) => {
			return {
				...content,
				plans: content.plans.map((plan) => {
					return {
						...plan,
					};
				}),
			};
		}),
	};
};

export const getBillingPeriod = (billingPeriod: string) => {
	if (billingPeriod) {
		if (billingPeriod === "MONTHLY") return "mies.";
		if (billingPeriod === "YEARLY") return "rok";
	}
};

export const drawSubscriptionDetailTitle = (name: string, quantity: number) => {
	if (quantity && quantity > 0) return `${name} (x${quantity})`;
	return name;
};

const OrganizationSubscriptionsEditPage = () => {
	const [loadingUnpaidProformas, setLoadingUnpaidProformas] = useState(false);
	const [subscriptions, setSubscriptions] = useState<SubscriptionsApi>();
	const [unpaidProformas, setUnpaidProformas] = useState<SubscriptionInvoiceApi[]>([]);
	const [settings, setSettings] = useState<AvailableSubscriptionContentsApi>();
	const [meDetails, setMeDetails] = useState<OrganizationMeApi | undefined>(undefined);
	const [showSummaryPreview, setShowSummaryPreview] = useState<boolean>(false);
	const [subscriptionsVisibility, setSubscriptionsVisibility] = useState<SubscriptionVisibility | undefined>();
	const [licenseLevels, setLicenseLevels] = useState([]);
	const { addFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const [errors, setErrors] = useState<undefined | ApiError[]>(undefined);
	const history = useHistory();
	const { t } = useTranslation();

	useEffect(() => {
		Promise.all([
			organizationApi.getSubscriptions(),
			organizationApi.getAvailableSubscriptionContents(),
			organizationApi.getSubscriptionsVisibility(),
		])
			.then(([subscriptions, contents, visibility]) => {
				setSettings(sortContents(contents.data.data.available_contents));
				const subs = {
					...subscriptions.data.data,
					subscriptions: subscriptions.data.data.subscriptions.map((x: SubscriptionApi) =>
						x.status === "CANCELLED" && x.cancellation_reason === "WAITING_PAYMENT"
							? {
									...x,
									cancelled_editable: true,
							  }
							: { ...x, cancelled_editable: false }
					),
				};
				setLicenseLevels(contents.data.data.license_levels);
				setSubscriptions(subs);
				if (visibility.visibility === SubscriptionVisibility.HIDDEN)
					history.push(`/${organization.id}/subscriptions`);
				setSubscriptionsVisibility(visibility.visibility);
			})
			.catch((err) => {
				const errors = handleException(err);
				setErrors(errors);
				handleError.alert(err, addFlash, ["customer_not_found"]);
			});
	}, []);

	useEffect(() => {
		fetchUnpaidInvoices();
	}, []);

	useEffect(() => {
		if (unpaidProformas.filter((x) => x.blocked).length > 0) history.push(`/${organization.id}/subscriptions`);
	}, [unpaidProformas]);

	const fetchUnpaidInvoices = async () => {
		setLoadingUnpaidProformas(true);
		try {
			const params: Record<string, any> = {
				f: btoa(
					unescape(
						encodeURIComponent(
							`${NEW_WAY_TO_ENCODING_FILTER_SIGN}status=NOT_PAID${FILTER_VALUE_SEPARATOR}PAYMENT_DUE`
						)
					)
				),
			};
			const res = await organizationApi.getSubscriptionInvoices(params);
			setUnpaidProformas(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoadingUnpaidProformas(false);
	};

	useEffect(() => {
		fetchOrganizationMeDetails();
	}, []);

	if (errors) {
		return <SubscriptionErrorsDisplay errors={errors} />;
	}

	if (subscriptionsVisibility === SubscriptionVisibility.HIDDEN) {
		return <SubscriptionsReadOnlyDisplay />;
	}

	const fetchOrganizationMeDetails = async () => {
		try {
			if (organization && organization.id) {
				const res = await organizationApi.getOrganizationMe(organization.id);
				setMeDetails(res);
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const goBack = () => {
		if (showSummaryPreview) setShowSummaryPreview(false);
		else history.push(`/${organization.id}/subscriptions`);
	};

	if (!subscriptions || !settings || !meDetails || loadingUnpaidProformas || !subscriptionsVisibility) {
		return <LoadingContainer />;
	}

	return (
		<>
			<Header
				title={t("common.word.subscriptions")}
				back={goBack}
				customHeaderContent={<SubscriptionsExpirationDate subscriptions={subscriptions.subscriptions} />}
			/>
			<SubscriptionsFormNew
				subscriptions={subscriptions}
				config={settings}
				meDetails={meDetails}
				subscriptionsVisibility={subscriptionsVisibility}
				licenseLevels={licenseLevels}
				setShowSummaryPreview={setShowSummaryPreview}
				showSummaryPreview={showSummaryPreview}
				invoices={unpaidProformas}
			/>
		</>
	);
};
export default OrganizationSubscriptionsEditPage;
